/* When the agent desk has an active callId, look up the call → customer.
 * Two-step fetch: /api/calls/:id resolves customer_id, then
 * /api/customers/:id pulls the full row (segment, account_number,
 * contract_end_date, etc. — fields the call detail doesn't carry). */

import { useEffect, useState } from "react";

import { apiFetch } from "../lib/api";

export type AgentDeskCustomer = {
  id: number;
  phone: string;
  account_number: string | null;
  business_name: string | null;
  first_name: string | null;
  last_name: string | null;
  billing_email: string | null;
  plan_id: number | null;
  plan: string | null;
  monthly_charge_cents: number;
  lines: number | null;
  tenure_months: number;
  segment: string | null;
  clv_score: number | null;
  prior_save_attempts: number | null;
  contract_end_date: string | null;
  churn_risk: number;
  status: string;
  cancel_reason: string | null;
  notes: string | null;
};

type CallLookup = {
  id: number;
  customer_id: number | null;
  from_number: string;
  to_number: string;
};

export type CustomerState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; customer: AgentDeskCustomer; verified: boolean }
  // Call exists but isn't linked to a customer record. We still expose
  // the from_number so the ProfileRail can show "Unknown caller (+1
  // 617-XXX-XXXX)" — gives the agent something to anchor on instead of
  // an opaque "Unknown caller" row.
  | { kind: "no-customer"; fromNumber: string | null }
  | { kind: "error"; message: string };

export function useCustomerForCall(callId: number | undefined): CustomerState {
  const [state, setState] = useState<CustomerState>({ kind: "idle" });

  useEffect(() => {
    if (callId === undefined) {
      setState({ kind: "idle" });
      return;
    }
    setState({ kind: "loading" });
    let cancelled = false;
    (async () => {
      try {
        const call = await apiFetch<CallLookup>(`/api/calls/${callId}`);
        if (cancelled) return;
        if (call.customer_id == null) {
          setState({ kind: "no-customer", fromNumber: call.from_number });
          return;
        }
        const customer = await apiFetch<AgentDeskCustomer>(
          `/api/customers/${call.customer_id}`,
        );
        if (cancelled) return;
        // "Verified via IVR" in the mockup = the inbound caller's phone
        // matched the customer record's phone. We only ever attach a
        // customer when that match succeeds, so verified is true here
        // unless the call was a synthetic /scenarios test (different leg).
        const verified =
          call.from_number === customer.phone ||
          call.to_number === customer.phone;
        setState({ kind: "ready", customer, verified });
      } catch (e) {
        if (!cancelled) {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callId]);

  return state;
}
